/// <reference types="bun-types" />
/**
 * Seeded start placement (`setupMatch`): picks each player's starting city
 * tile from the match seed, preferring inland spots with food and wood in
 * the starting territory and rivals far apart, relaxing step by step when
 * the island can't fit that. Same seed and roster, same starts on every peer.
 */
import seedrandom from 'seedrandom';
import { axialHexDistance, axialToWorld } from '../hexmap/coords';
import { getHexTileMapKey } from '../hexmap/model/keys';
import { axialNeighbors, hexesWithinRadius } from '../hexmap/model/map';
import type { HexTileCoordinates, HexTileMap } from '../hexmap/model/types';
import type { RuleSet } from '../rules';
import { minCityDistance } from './placement';

// ── Module constants ─────────────────────────────────────────────────────────

const LAND_TYPES = ['default', 'field', 'forest', 'beach'] as const;
const MIN_LANDMASS_K = 14;
const BFS_CAP = 64;
const TERRITORY_SCORE_RADIUS_BONUS = 2;
const FAIRNESS_TOLERANCE = 1.5;
const MAX_ROUNDS = 20;
const EDGE_MARGIN = 2;
const CANDIDATES_PER_SECTOR = 24;
// Preferred-rung requirements: a starting territory must hold both food and
// wood tiles, and rivals should start a fair way apart. Relaxed if unmet.
const MIN_TERRITORY_FIELDS = 2;
const MIN_TERRITORY_FORESTS = 2;
const PREFERRED_SPACING_FRACTION = 0.5; // of map radius
// Rivals never start closer than this (hexes) while any layout allows it:
// past the city's minDistanceToCity (5), so an early rush has a walk to make.
// The last fallbacks relax it to minDistanceToCity, then drop it.
const MIN_START_SPACING = 8;
const PREFERRED_CANDIDATE_FRACTION = 0.5; // of each sector's mixed tiles

const TWO_PI = 2 * Math.PI;

// ── Types ────────────────────────────────────────────────────────────────────

interface PlayerPlacement {
  playerId: string;
  cityPos: HexTileCoordinates;
}

interface Candidate {
  coord: HexTileCoordinates;
  key: string;
  score: number;
  landmassOk: boolean;
  edgeOk: boolean;
}

// ── Private helpers ──────────────────────────────────────────────────────────

/** Normalise atan2 angle into [0, 2π) after subtracting rotation. */
function tileAngle(coord: HexTileCoordinates, rotation: number): number {
  const { x, z } = axialToWorld(coord, 1);
  const raw = Math.atan2(z, x);
  return (((raw - rotation) % TWO_PI) + TWO_PI) % TWO_PI;
}

/** Return which sector (0-indexed) a normalised angle falls in. */
function sectorOf(angle: number, n: number): number {
  return Math.min(Math.floor(angle / (TWO_PI / n)), n - 1);
}

/**
 * Collect all land tiles from the map, sorted ascending by key string.
 * Never iterates the Map for decisions — builds the sorted array once.
 */
function collectLandTiles(
  terrain: HexTileMap
): Array<{ coord: HexTileCoordinates; key: string; resourceValue: number }> {
  const out: Array<{ coord: HexTileCoordinates; key: string; resourceValue: number }> = [];
  for (const [key, tile] of terrain) {
    if ((LAND_TYPES as readonly string[]).includes(tile.type)) {
      out.push({ coord: tile.coordinates, key, resourceValue: tile.resourceValue });
    }
  }
  out.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  return out;
}

/**
 * BFS to check whether ≥ k land tiles are reachable from coord within BFS_CAP visited.
 */
function landmassAtLeast(terrain: HexTileMap, coord: HexTileCoordinates, k: number): boolean {
  const visited = new Set<string>();
  const queue: HexTileCoordinates[] = [coord];
  visited.add(getHexTileMapKey(coord));
  let count = 0;
  while (queue.length > 0 && visited.size <= BFS_CAP) {
    const cur = queue.shift();
    if (cur === undefined) break;
    count++;
    if (count >= k) return true;
    for (const nb of axialNeighbors(cur)) {
      const nbKey = getHexTileMapKey(nb);
      if (visited.has(nbKey)) continue;
      visited.add(nbKey);
      const tile = terrain.get(nbKey);
      if (tile && (LAND_TYPES as readonly string[]).includes(tile.type)) {
        queue.push(nb);
      }
    }
  }
  return count >= k;
}

/**
 * Return false if any hex within EDGE_MARGIN of coord is absent from the terrain map.
 */
function isNearEdge(terrain: HexTileMap, coord: HexTileCoordinates): boolean {
  for (const nb of hexesWithinRadius(coord, EDGE_MARGIN)) {
    if (!terrain.has(getHexTileMapKey(nb))) return true;
  }
  return false;
}

/**
 * Sum resourceValue over land tiles within radius R of a candidate coord.
 */
function territoryScore(terrain: HexTileMap, coord: HexTileCoordinates, r: number): number {
  let total = 0;
  for (const nb of hexesWithinRadius(coord, r)) {
    const tile = terrain.get(getHexTileMapKey(nb));
    if (tile && (LAND_TYPES as readonly string[]).includes(tile.type)) {
      total += tile.resourceValue;
    }
  }
  return total;
}

/**
 * True if the territory disc around coord holds enough food and wood tiles.
 */
function hasResourceMix(terrain: HexTileMap, coord: HexTileCoordinates, r: number): boolean {
  let fields = 0;
  let forests = 0;
  for (const nb of hexesWithinRadius(coord, r)) {
    const type = terrain.get(getHexTileMapKey(nb))?.type;
    if (type === 'field') fields++;
    else if (type === 'forest') forests++;
  }
  return fields >= MIN_TERRITORY_FIELDS && forests >= MIN_TERRITORY_FORESTS;
}

/** Largest axial distance from the origin to any tile (the map's radius). */
function mapRadius(terrain: HexTileMap): number {
  let max = 0;
  for (const tile of terrain.values()) {
    max = Math.max(max, axialHexDistance({ q: 0, r: 0 }, tile.coordinates));
  }
  return max;
}

/**
 * Build a sorted, capped candidate list from a set of land tiles.
 * sortedLand must already be sorted by key ascending.
 */
function buildCandidates(
  terrain: HexTileMap,
  sortedLand: Array<{ coord: HexTileCoordinates; key: string; resourceValue: number }>,
  scoreRadius: number,
  cap: number
): Candidate[] {
  // Pre-filter: sort by resourceValue desc, key asc; take top cap*2
  const prefiltered = sortedLand
    .slice()
    .sort((a, b) =>
      b.resourceValue !== a.resourceValue
        ? b.resourceValue - a.resourceValue
        : a.key < b.key
          ? -1
          : a.key > b.key
            ? 1
            : 0
    );
  const pool = prefiltered.slice(0, cap * 2);

  const candidates: Candidate[] = pool.map(({ coord, key }) => ({
    coord,
    key,
    score: territoryScore(terrain, coord, scoreRadius),
    landmassOk: landmassAtLeast(terrain, coord, MIN_LANDMASS_K),
    edgeOk: !isNearEdge(terrain, coord),
  }));

  // Sort by score desc, key asc
  candidates.sort((a, b) =>
    b.score !== a.score ? b.score - a.score : a.key < b.key ? -1 : a.key > b.key ? 1 : 0
  );

  return candidates.slice(0, cap);
}

/**
 * Run up to MAX_ROUNDS placement rounds.
 * Returns [choices in sector order, fairness ratio] or null if no full set found.
 * Options: requireEdge, requireSector, requireLandmass, requireFairness.
 */
function runPlacementRounds(
  sectorCandidates: Candidate[][],
  minDist: number,
  rng: () => number,
  options: {
    requireEdge: boolean;
    requireLandmass: boolean;
    requireFairness: boolean;
  }
): HexTileCoordinates[] | null {
  const n = sectorCandidates.length;
  let bestChoices: HexTileCoordinates[] | null = null;
  let bestRatio = Infinity;

  for (let round = 0; round < MAX_ROUNDS; round++) {
    const chosen: HexTileCoordinates[] = [];
    let roundOk = true;

    for (let s = 0; s < n; s++) {
      const pool = sectorCandidates[s].filter((c) => {
        if (options.requireLandmass && !c.landmassOk) return false;
        if (options.requireEdge && !c.edgeOk) return false;
        for (const prev of chosen) {
          if (axialHexDistance(c.coord, prev) < minDist) return false;
        }
        return true;
      });

      if (pool.length === 0) {
        roundOk = false;
        break;
      }
      const pick = pool[Math.floor(rng() * pool.length)];
      chosen.push(pick.coord);
    }

    if (!roundOk) continue;

    // Compute fairness ratio
    const scores = chosen.map((coord) => {
      // We need the score — find it in sector candidates
      for (const candidates of sectorCandidates) {
        const c = candidates.find((x) => x.key === getHexTileMapKey(coord));
        if (c) return c.score;
      }
      return 0;
    });
    const maxScore = Math.max(...scores);
    const minScore = Math.min(...scores);
    const ratio = maxScore / Math.max(1, minScore);

    if (options.requireFairness && ratio <= FAIRNESS_TOLERANCE) {
      return chosen;
    }

    if (!options.requireFairness) {
      return chosen;
    }

    // Keep best-so-far (strictly smaller ratio wins; earlier round wins ties)
    if (ratio < bestRatio) {
      bestRatio = ratio;
      bestChoices = chosen;
    }
  }

  return bestChoices;
}

/**
 * Full placement algorithm including relaxation ladder.
 * Returns coords in sortedIds order.
 */
function placeWithRelaxation(
  terrain: HexTileMap,
  rng: () => number,
  rotation: number,
  sortedIds: string[],
  minDist: number,
  scoreRadius: number,
  territoryRadius: number,
  floorDist = minDist
): HexTileCoordinates[] {
  const n = sortedIds.length;

  if (n === 0) return [];

  // Collect all sorted land tiles
  const sortedLand = collectLandTiles(terrain);
  // Preferred city sites: inland (not beach) with food and wood in territory.
  const mixedLand = sortedLand.filter(
    (t) =>
      terrain.get(getHexTileMapKey(t.coord))?.type !== 'beach' &&
      hasResourceMix(terrain, t.coord, territoryRadius)
  );

  if (n === 1) {
    // Solo: prefer a start with both food and wood in territory.
    for (const c of buildCandidates(terrain, mixedLand, scoreRadius, CANDIDATES_PER_SECTOR)) {
      if (c.landmassOk && c.edgeOk) return [c.coord];
    }
    // Otherwise: all land tiles are one sector; pick top candidate with landmassOk && edgeOk
    const candidates = buildCandidates(terrain, sortedLand, scoreRadius, CANDIDATES_PER_SECTOR);

    for (const c of candidates) {
      if (c.landmassOk && c.edgeOk) return [c.coord];
    }
    // Relaxation: drop edgeOk
    for (const c of candidates) {
      if (c.landmassOk) return [c.coord];
    }
    // Relaxation: drop landmassOk
    for (const c of candidates) {
      return [c.coord];
    }
    // Last resort: any land tile
    if (sortedLand.length > 0) return [sortedLand[0].coord];
    // No land tiles at all — reuse origin (should never happen on valid maps)
    return [{ q: 0, r: 0 }];
  }

  // N >= 2: build per-sector candidate lists
  const buildSectorCandidates = (
    includeOrigin: boolean,
    land = sortedLand,
    cap: (sectorSize: number) => number = () => CANDIDATES_PER_SECTOR
  ): Candidate[][] => {
    const sectorTiles: Array<
      Array<{ coord: HexTileCoordinates; key: string; resourceValue: number }>
    > = Array.from({ length: n }, () => []);

    for (const tile of land) {
      const { q, r } = tile.coord;
      if (!includeOrigin && q === 0 && r === 0) continue;
      const angle = tileAngle(tile.coord, rotation);
      const s = sectorOf(angle, n);
      sectorTiles[s].push(tile);
    }

    return sectorTiles.map((tiles) =>
      buildCandidates(terrain, tiles, scoreRadius, cap(tiles.length))
    );
  };

  // Preferred rungs: resource-mixed starts, first spaced well apart, then at
  // the minimum distance. Every mixed tile is a candidate (not just the top
  // resourceValue tiles, which cluster at the island centre and defeat the
  // spacing). With fairness requested, runPlacementRounds returns the fairest
  // valid round even when none meets the tolerance — any mixed layout beats
  // the fallbacks below.
  {
    const preferredDist = Math.max(
      minDist,
      Math.round(mapRadius(terrain) * PREFERRED_SPACING_FRACTION)
    );
    // Better-scoring half of each sector's mixed tiles: wide enough to allow
    // spacing, but trims weak coastal starts so fair rounds stay likely.
    const sectorCandidates = buildSectorCandidates(false, mixedLand, (size) =>
      Math.ceil(size * PREFERRED_CANDIDATE_FRACTION)
    );
    for (const dist of [preferredDist, minDist]) {
      const result = runPlacementRounds(sectorCandidates, dist, rng, {
        requireEdge: true,
        requireLandmass: true,
        requireFairness: true,
      });
      if (result !== null) return result;
    }
  }

  // Rung 0: full constraints (fairness required)
  {
    const sectorCandidates = buildSectorCandidates(false);
    const result = runPlacementRounds(sectorCandidates, minDist, rng, {
      requireEdge: true,
      requireLandmass: true,
      requireFairness: true,
    });
    if (result !== null) {
      // Check if ratio <= FAIRNESS_TOLERANCE
      const scores = result.map((coord) => {
        const key = getHexTileMapKey(coord);
        for (const candidates of sectorCandidates) {
          const c = candidates.find((x) => x.key === key);
          if (c) return c.score;
        }
        return 0;
      });
      const maxScore = Math.max(...scores);
      const minScore = Math.min(...scores);
      const ratio = maxScore / Math.max(1, minScore);
      if (ratio <= FAIRNESS_TOLERANCE) return result;
    }
  }

  // Rung 1: drop fairness
  {
    const sectorCandidates = buildSectorCandidates(false);
    const result = runPlacementRounds(sectorCandidates, minDist, rng, {
      requireEdge: true,
      requireLandmass: true,
      requireFairness: false,
    });
    if (result !== null) return result;
  }

  // Rung 2: also ignore edgeOk
  {
    const sectorCandidates = buildSectorCandidates(false);
    const result = runPlacementRounds(sectorCandidates, minDist, rng, {
      requireEdge: false,
      requireLandmass: true,
      requireFairness: false,
    });
    if (result !== null) return result;
  }

  // Rung 3: also drop sector restriction — global candidate list, greedy
  {
    const globalLand = sortedLand; // includes origin
    const globalCandidates = buildCandidates(
      terrain,
      globalLand,
      scoreRadius,
      CANDIDATES_PER_SECTOR * 4
    );
    for (const dist of new Set([minDist, floorDist])) {
      const chosen: HexTileCoordinates[] = [];

      for (let i = 0; i < n; i++) {
        const available = globalCandidates.filter((c) => {
          if (!c.landmassOk) return false;
          for (const prev of chosen) {
            if (axialHexDistance(c.coord, prev) < dist) return false;
          }
          // Not already chosen
          const key = getHexTileMapKey(c.coord);
          return !chosen.some((p) => getHexTileMapKey(p) === key);
        });
        if (available.length === 0) break;
        chosen.push(available[0].coord);
      }

      if (chosen.length === n) return chosen;
    }
  }

  // Rung 4: also drop landmassOk
  {
    const globalCandidates = buildCandidates(
      terrain,
      sortedLand,
      scoreRadius,
      CANDIDATES_PER_SECTOR * 4
    );
    for (const dist of new Set([minDist, floorDist])) {
      const chosen: HexTileCoordinates[] = [];

      for (let i = 0; i < n; i++) {
        const available = globalCandidates.filter((c) => {
          for (const prev of chosen) {
            if (axialHexDistance(c.coord, prev) < dist) return false;
          }
          const key = getHexTileMapKey(c.coord);
          return !chosen.some((p) => getHexTileMapKey(p) === key);
        });
        if (available.length === 0) break;
        chosen.push(available[0].coord);
      }

      if (chosen.length === n) return chosen;
    }
  }

  // Rung 5: last resort — drop minDist
  // Place each remaining player on best-scoring unused land tile
  {
    // Build sorted land by score desc, key asc
    const scoredLand = sortedLand.map((t) => ({
      coord: t.coord,
      key: t.key,
      score: territoryScore(terrain, t.coord, scoreRadius),
    }));
    scoredLand.sort((a, b) =>
      b.score !== a.score ? b.score - a.score : a.key < b.key ? -1 : a.key > b.key ? 1 : 0
    );

    const usedKeys = new Set<string>();
    const chosen: HexTileCoordinates[] = [];

    for (let i = 0; i < n; i++) {
      const pick = scoredLand.find((t) => !usedKeys.has(t.key));
      if (pick) {
        usedKeys.add(pick.key);
        chosen.push(pick.coord);
      } else if (scoredLand.length > 0) {
        // More players than land tiles — reuse top tile (never throws; documented)
        chosen.push(scoredLand[0].coord);
      } else {
        // Zero land tiles at all (pathological all-water map): distinct
        // synthetic positions so players never stack on one phantom tile.
        chosen.push({ q: i, r: 0 });
      }
    }

    return chosen;
  }
}

/**
 * Map sector-ordered coords back to sortedIds, then reorder to input playerIds order.
 */
function finalize(
  sectorCoords: HexTileCoordinates[],
  sortedIds: string[],
  playerIds: string[]
): PlayerPlacement[] {
  // sortedIds[i] gets sectorCoords[i]
  const byId = new Map<string, HexTileCoordinates>();
  for (let i = 0; i < sortedIds.length; i++) {
    byId.set(sortedIds[i], sectorCoords[i]);
  }

  return playerIds.map((playerId) => ({
    playerId,
    // byId is populated for every sortedId which covers all playerIds — safe cast
    cityPos: byId.get(playerId) as HexTileCoordinates,
  }));
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Deterministically place city starting positions for all players on a hex map.
 *
 * - Always returns one placement per input player.
 * - Never throws on any terrain.
 * - Output array is ordered to match input playerIds order.
 */
function setupMatch(
  terrain: HexTileMap,
  seed: string,
  playerIds: string[],
  rules: RuleSet
): PlayerPlacement[] {
  if (playerIds.length === 0) return [];

  const cityDef = rules.buildings.city;
  const cityMinDist = minCityDistance(rules);
  const minDist = Math.max(cityMinDist, MIN_START_SPACING);
  const territoryRadius = cityDef?.territoryRadius ?? 3;
  const scoreRadius = territoryRadius + TERRITORY_SCORE_RADIUS_BONUS;

  // Single PRNG — rotation drawn FIRST exactly once
  const rng = seedrandom(seed);
  const rotation = rng() * TWO_PI;

  const sortedIds = playerIds.slice().sort();
  const sectorCoords = placeWithRelaxation(
    terrain,
    rng,
    rotation,
    sortedIds,
    minDist,
    scoreRadius,
    territoryRadius,
    cityMinDist
  );

  return finalize(sectorCoords, sortedIds, playerIds);
}

export type { PlayerPlacement };
export { setupMatch };
